import React from "react";
import { PageShell } from "@/components/Layout";
import Seo from "@/components/Seo";

/**
 * Shared layout for all legal pages. Keeps copy consistent and typographically
 * aligned with the rest of the site. Content is PLACEHOLDER — tone-of-voice
 * drafts intended to be reviewed by legal counsel before launch.
 */
export function LegalPage({ title, slug, updated, children, description }) {
  return (
    <PageShell>
      <Seo
        title={title}
        description={description || `${title} — anArtizen.`}
        canonical={`https://anartizen.com/${slug}`}
      />
      <section className="relative grid-bg border-b border-border">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 pt-20 pb-10">
          <p className="meta-mono mb-4 !text-primary">— LEGAL · {slug.toUpperCase()}</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] tracking-tighter">
            {title}
          </h1>
          <p className="meta-mono mt-5 !text-white/55">— LAST UPDATED {updated}</p>
        </div>
      </section>
      <section>
        <div className="max-w-[800px] mx-auto px-5 md:px-10 py-14 legal-prose">
          {children}
        </div>
      </section>
    </PageShell>
  );
}

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy" slug="privacy" updated="OCT 2026"
      description="How anArtizen collects, uses and protects your personal information.">
      <p>anArtizen (&ldquo;we&rdquo;, &ldquo;us&rdquo;) respects your privacy. This policy explains what we collect, why, and how to control it. If you have questions, write to <a href="mailto:hello@anartizen.com">hello@anartizen.com</a>.</p>

      <h2>Information we collect</h2>
      <ul>
        <li><strong>Account:</strong> name, email and Google profile picture (when you sign in with Google).</li>
        <li><strong>Bookings:</strong> name, phone, city, discipline, your written answers to the two intake questions, and payment status (via Razorpay — card data never touches our servers).</li>
        <li><strong>Early-access RSVPs:</strong> name, email, city, discipline and your optional one-line wish.</li>
        <li><strong>Technical:</strong> standard server logs, cookies, and basic analytics (page views, device type).</li>
      </ul>

      <h2>How we use it</h2>
      <ul>
        <li>To deliver sessions you booked (calendar invite, reminders, recordings link).</li>
        <li>To tell you when a specific advisor opens their calendar (if you opted in).</li>
        <li>To send occasional updates about Unfair Advantage, No Pressure and Build. One-click unsubscribe at the bottom of every email.</li>
        <li>To improve the product and the roster — never to sell your data.</li>
      </ul>

      <h2>Who we share it with</h2>
      <ul>
        <li><strong>Razorpay</strong> for payment processing.</li>
        <li><strong>Google</strong> (Calendar, Sign-In) when you book a session or log in.</li>
        <li><strong>Resend</strong> for transactional and marketing email delivery.</li>
        <li><strong>Advisors you book:</strong> we share your name, email and your written intake answers so they can prepare.</li>
      </ul>

      <h2>Your rights</h2>
      <p>You can request a copy of your data, correct it, or ask us to delete it entirely. Write to <a href="mailto:hello@anartizen.com">hello@anartizen.com</a> from the email on file and we&apos;ll act within 15 business days.</p>

      <h2>Retention</h2>
      <p>Booking records are kept for 7 years for tax compliance. Marketing emails stop the moment you unsubscribe. RSVP records are deleted on request.</p>

      <h2>Cookies</h2>
      <p>We use strictly necessary cookies for login and anti-abuse, plus basic analytics cookies. We do not use advertising trackers.</p>

      <h2>Contact</h2>
      <p>Grievance Officer: Meher, <a href="mailto:hello@anartizen.com">hello@anartizen.com</a>. Mumbai, India.</p>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of Service" slug="terms" updated="OCT 2026"
      description="The terms that govern your use of anArtizen.">
      <p>By using anArtizen you agree to these terms. If you don&apos;t agree, don&apos;t use the service. Written at a human reading level; a formal legal review is in progress.</p>

      <h2>Eligibility</h2>
      <p>You must be at least 18 years old (or using with a guardian) and able to form a legally binding contract where you live.</p>

      <h2>Your account</h2>
      <p>You&apos;re responsible for the information you provide and the activity on your account. Don&apos;t impersonate anyone. Keep your login secure.</p>

      <h2>What we do</h2>
      <p>anArtizen operates three surfaces today: <strong>Unfair Advantage</strong> (paid 1-on-1 advisor sessions), <strong>No Pressure</strong> (free educational content), and <strong>Build</strong> (project intake for artists and brands). We&apos;re a facilitator — advisors are independent professionals, not employees of anArtizen.</p>

      <h2>Payments &amp; cancellations</h2>
      <p>All session fees are inclusive of applicable taxes shown at checkout. Payment is captured upfront via Razorpay. For reschedules and cancellations see our <a href="/refunds">Refunds Policy</a>.</p>

      <h2>Advisor conduct</h2>
      <p>Advisors agree to a code of conduct (be present, be prepared, no discrimination). If an advisor behaves inappropriately, email <a href="mailto:hello@anartizen.com">hello@anartizen.com</a> within 7 days of the session and we&apos;ll investigate and refund where appropriate.</p>

      <h2>Content &amp; intellectual property</h2>
      <p>Anything you share in a session or a brief remains yours. We don&apos;t claim ownership over your work. Material published on anArtizen.com (writing, images, structure) is ours or licensed to us; please don&apos;t republish without written permission.</p>

      <h2>Prohibited use</h2>
      <ul>
        <li>Scraping, reverse engineering, or abuse of the service.</li>
        <li>Harassment, hate speech, or illegal activity.</li>
        <li>Using advisor sessions for anything other than genuine professional advice (e.g. no cold recruiting, no solicitation).</li>
      </ul>

      <h2>Liability</h2>
      <p>We provide the service &ldquo;as is&rdquo;. We are not liable for business decisions you take based on an advisor&apos;s input. Our aggregate liability is capped at the fees you paid in the preceding 12 months.</p>

      <h2>Changes</h2>
      <p>We may update these terms. We&apos;ll flag material changes by email where possible.</p>

      <h2>Jurisdiction</h2>
      <p>These terms are governed by the laws of India. Courts in Mumbai have exclusive jurisdiction.</p>
    </LegalPage>
  );
}

export function Refunds() {
  return (
    <LegalPage title="Refunds &amp; Reschedules" slug="refunds" updated="OCT 2026"
      description="How reschedules, cancellations and refunds work for Unfair Advantage sessions.">
      <p>We work in a fluid industry. Here&apos;s how we handle changes.</p>

      <h2>Reschedules</h2>
      <ul>
        <li><strong>One free reschedule</strong> per booking, from your dashboard, up to 24 hours before the session.</li>
        <li>Second reschedule onwards: email <a href="mailto:hello@anartizen.com">hello@anartizen.com</a> — we handle it case by case.</li>
      </ul>

      <h2>Cancellations</h2>
      <ul>
        <li>You can cancel any time to free the slot for another member.</li>
        <li>Session fees are <strong>non-refundable</strong> once paid, except in the cases below.</li>
      </ul>

      <h2>When we refund</h2>
      <p>We&apos;ll issue a full refund within 10 business days when:</p>
      <ul>
        <li>The advisor cancels and no suitable alternative slot is available within 30 days.</li>
        <li>A technical failure on our side prevents the session from happening.</li>
        <li>You report advisor misconduct within 7 days of the session and we agree.</li>
      </ul>

      <h2>How to request</h2>
      <p>Email <a href="mailto:hello@anartizen.com">hello@anartizen.com</a> with your booking ID (ANZ-XXXX-…) and a one-line reason. We reply within 2 business days.</p>

      <h2>Payment partner</h2>
      <p>All refunds are processed to the original payment method via Razorpay. Settlement time depends on your bank (usually 5-7 business days).</p>
    </LegalPage>
  );
}

export function Contact() {
  return (
    <LegalPage title="Contact" slug="contact" updated="OCT 2026"
      description="How to reach anArtizen for support, press, partnerships or legal matters.">
      <p>The fastest way to reach us is email. We&apos;re a small team based in Mumbai; we usually reply within one business day.</p>

      <h2>General &amp; support</h2>
      <p><a href="mailto:hello@anartizen.com">hello@anartizen.com</a></p>

      <h2>Press &amp; partnerships</h2>
      <p><a href="mailto:hello@anartizen.com">hello@anartizen.com</a> &mdash; subject line <code>PRESS</code> or <code>PARTNERSHIP</code>.</p>

      <h2>Legal &amp; privacy</h2>
      <p>Grievance Officer: Meher. <a href="mailto:hello@anartizen.com">hello@anartizen.com</a></p>

      <h2>Mailing address</h2>
      <p>anArtizen<br/>Mumbai, Maharashtra<br/>India</p>

      <p className="mt-10 meta-mono !text-white/50">— WE&apos;RE QUIET ON INSTAGRAM. WE REPLY TO EMAIL.</p>
    </LegalPage>
  );
}

export function NotFound() {
  return (
    <PageShell>
      <Seo title="404 — Not Found" description="This page does not exist on anArtizen." />
      <section className="relative grid-bg min-h-[72vh] flex items-center">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-20 w-full">
          <p className="meta-mono mb-6 !text-primary">— 404 · PAGE NOT FOUND</p>
          <h1 className="font-display text-6xl md:text-8xl font-bold leading-[0.9] tracking-tighter">
            This page is <span className="hl-coral">not on the map.</span>
          </h1>
          <p className="mt-8 max-w-xl text-white/75 text-base md:text-lg leading-relaxed">
            Either we moved it, or the link you followed is stale. Try one of these instead:
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="/" className="btn-pill" data-testid="404-home">Home</a>
            <a href="/unfair-advantage" className="btn-pill btn-coral" data-testid="404-ua">Book a session</a>
            <a href="/no-pressure" className="btn-pill" data-testid="404-np">Learn</a>
            <a href="/build" className="btn-pill" data-testid="404-build">Build</a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
